import { basePath } from "@Common/constants";
import { FeatureCard } from "@Src/components/FeatureCard";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { RetrowaveBackground } from "@Src/components/RetrowaveBackground";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

export const PublicHomepage = () => {
	const navigate = useNavigate();

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "background.default",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<PublicNav />

			<RetrowaveBackground />

			<Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
				{/* Hero */}
				<Box
					sx={{
						maxWidth: 800,
						mx: "auto",
						textAlign: "center",
						pt: { xs: 8, md: 12 },
						pb: { xs: 8, md: 10 },
					}}
				>
					<Typography
						variant="h1"
						sx={{
							fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
							fontWeight: 700,
							color: "text.primary",
							mb: 3,
							lineHeight: 1.2,
						}}
					>
						AI-free Online Forms
					</Typography>

					<Typography
						variant="h5"
						sx={{
							fontSize: { xs: "1.125rem", md: "1.25rem" },
							color: "text.secondary",
							mb: 5,
							fontWeight: 400,
							lineHeight: 1.6,
						}}
					>
						Assess knowledge, not AI prompt skills. <br />
						NoMachine helps universities and companies create secure online
						forms that make AI assistance more easily detectable, ensuring
						authentic knowledge evaluation.
					</Typography>

					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<Button
							variant="contained"
							size="large"
							onClick={() => navigate(basePath)}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Get Started
						</Button>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/features")}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Learn More
						</Button>
					</Box>
				</Box>

				{/* Feature section 1 */}
				<Box sx={{ pb: { xs: 8, md: 10 } }}>
					<Box
						sx={{
							maxWidth: 700,
							mx: "auto",
							textAlign: "center",
							mb: 8,
						}}
					>
						<Typography
							variant="h2"
							sx={{
								fontSize: { xs: "2rem", md: "2.5rem" },
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Built for Authentic Assessment
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								fontSize: "1.125rem",
							}}
						>
							Our platform makes it simple to create and manage online forms
							that collect AI-free answers. Whether you're a startup, a
							university, a financial institution, a large company, use
							NoMachine for your AI-free online forms!
						</Typography>
					</Box>

					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SecurityIcon fontSize="inherit" />}
								title="AI Prevention"
								description="Advanced monitoring that enables forms creators to detect use of external tools such as AI."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SpeedIcon fontSize="inherit" />}
								title="Easy Setup"
								description="Create your assessment forms in minutes with our intuitive interface. No technical expertise required."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<AssessmentIcon fontSize="inherit" />}
								title="Real-Time Monitoring"
								description="Track responses as they come in and analyze results with powerful reporting tools."
							/>
						</Grid>
					</Grid>
				</Box>

				{/* Feature section 2 */}
				<br />
				<Box sx={{ pb: { xs: 8, md: 10 } }}>
					<Box
						sx={{
							maxWidth: 700,
							mx: "auto",
							textAlign: "center",
							mb: 8,
						}}
					>
						<Typography
							variant="h2"
							sx={{
								fontSize: { xs: "2rem", md: "2.5rem" },
								fontWeight: 600,
								color: "text.primary",
							}}
						>
							Why Organizations Choose NoMachine
						</Typography>
					</Box>

					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SupportAgentIcon fontSize="inherit" />}
								title="Reliable Support"
								description="Our team is here to help you get the most out of your assessments with dedicated customer support."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SecurityIcon fontSize="inherit" />}
								title="Data Security"
								description="Your assessment data is encrypted and secure. We follow industry best practices to protect your information."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<AssessmentIcon fontSize="inherit" />}
								title="Actionable Insights"
								description="Get detailed analytics and insights about your assessments to make informed decisions."
							/>
						</Grid>
					</Grid>
				</Box>

				{/* CTA */}
				<br />
				<Box sx={{ textAlign: "center", pb: { xs: 8, md: 12 } }}>
					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: "2rem", md: "2.5rem" },
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Ready to Create Authentic Assessments?
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 4,
							fontSize: "1.125rem",
						}}
					>
						Join institutions who trust NoMachine to maintain the integrity of
						their evaluations.
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<Button
							variant="contained"
							size="large"
							onClick={() => navigate(basePath)}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Sign In
						</Button>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/pricing")}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							View Pricing
						</Button>
					</Box>
				</Box>
			</Container>

			<PublicFooter />
		</Box>
	);
};
